import { Button, Descriptions, Space, Typography, message } from 'antd';
import axios from 'axios';
import { useContext, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2, BsImages, BsRulers } from 'react-icons/bs';
import { useHistory } from 'react-router-dom';

import ProductImage from '../../components/productImage';
import { clearObjFields, sizeEnumDictionary, weightEnumDictionary } from '../../utils';
import { convertToCm, convertToGramm, formatCurrency, formatVolume } from '.././../utils/format';
import { descriptionProps, val, YesNo } from './components/descriptions';
import FormFooter from './components/FormFooter';
import SectionCard from './components/SectionCard';
import { clearDraft } from './draft';
import { LabelContext } from './ProductDataContext';
import './Product.less';

const { Text } = Typography;

const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

const ProductCheck = ({ current, steps, prev }) => {
  const history = useHistory();
  const productContext = useContext(LabelContext);
  const sender = productContext.labelInfo.productItem;
  const version = productContext.variantList.prop;
  const isErlian = productContext.isErlian;

  const getBaseProduct = () => {
    let baseproduct = {
      price: sender.price || 0,
      isReturnable: sender.hasReturn,
      barCodes: sender.barCode || [],
      description: !isErlian ? sender.detail : 'ereenhot',
      stock: 0,
      brand: sender.brand,
      imageCover: sender.image,
      images: productContext.otherImages?.map((el) => el?.url),
      productSKU: productContext.productBaseSku,
      minStock: sender.minStock,
      reorderQuantity: sender.reorderQuantity,
    };

    if (sender.type || sender.size || sender.color) {
      baseproduct.attributes = {
        type: sender.type,
        size: sender.size,
        color: sender.color,
      };
      baseproduct.attributes = clearObjFields(baseproduct.attributes);
    }

    return baseproduct;
  };

  const getProductVariants = () => {
    let products = version.map((item) => {
      let product = {
        price: item?.price,
        barCodes: item?.barcode || [],
        stock: 0,
        productSKU: item?.productSKU,
        imageCover: item.image?.[0]?.url ?? '',
        images: item.images?.map((el) => el?.url),
        description: sender.detail,
      };

      if (item.type || item.size || item.color) {
        product.attributes = {
          type: item.type,
          size: item.size,
          color: item.color,
        };
        product.attributes = clearObjFields(product.attributes);
      }
      return clearObjFields(product);
    });

    return products ?? [];
  };

  // Давхар дарахад бараа хоёр удаа үүсэхээс сэргийлнэ
  const [submitting, setSubmitting] = useState(false);

  const onHandleSubmit = async () => {
    if (submitting) return;
    let payload = {
      category: sender.category,
      title: sender.title,
      minStock: sender.minStock,
      reorderQuantity: sender.reorderQuantity,
      baseSku: productContext.productBaseSku,
      description: !isErlian ? sender?.detail : '_ereenhot',
      weight: convertToGramm(sender.weightUnit, sender.weight),
      weightMeasure: sender?.weightUnit || 'g',
      sizeMeasure: sender?.volumeUnit || 'cm',
      willExpire: sender.willExpire,
      isReturnable: sender.hasReturn,
      imageCover: sender.image,
      isGroup: false,
      productList: version?.length ? getProductVariants() : [getBaseProduct()],
      isCBM: sender.isCBM,
      isFragile: sender.isFragile,
    };

    if (sender.long && sender.wide && sender.height && !sender.isCBM) {
      payload.size = {
        length: convertToCm(sender.volumeUnit, sender.long),
        width: convertToCm(sender.volumeUnit, sender.wide),
        height: convertToCm(sender.volumeUnit, sender.height),
      };
    }
    if (sender.isCBM && sender.cbm) {
      payload.size = {
        length: convertToCm(sender.volumeUnit, sender.cbm),
        width: convertToCm(sender.volumeUnit, 1),
        height: convertToCm(sender.volumeUnit, 1),
      };
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`/api/v1.0/product`, clearObjFields(payload));
      if (response.data.statusCode === 200) {
        // Бараа үүссэн — хагас дутуу мэдээллийн draft хэрэггүй болно
        clearDraft(productContext.draftKey);
        history.push('/merchant/product/list');
        message.success('Бараа амжилттай үүслээ!');
      }
    } catch (e) {
      console.error('Create product error: ', e);
    } finally {
      setSubmitting(false);
    }
  };

  const volume = sender.isCBM ? sender.cbm : sender.long * sender.wide * sender.height;

  /** Сонгосон хэмжих нэгжийн товчлол (см, кг гэх мэт) */
  const sizeUnit = sizeEnumDictionary[sender.volumeUnit] ?? sender.volumeUnit;
  const weightUnit = weightEnumDictionary[sender.weightUnit] ?? sender.weightUnit;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <SectionCard icon={<BsBox2 />} title="Барааны мэдээлэл">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, xl: 3 }}
          items={[
            { key: 'title', label: 'Барааны нэр', children: val(sender.title) },
            { key: 'brand', label: 'Барааны бренд', children: val(sender.brand) },
            {
              key: 'category',
              // Жагсаалттай ижилхэн НЭРЭЭР харуулна. Нэр нь мэдэгдэхгүй хуучин
              // draft-ууд дээр л кодоор нөхөж харуулна.
              label: 'Категори',
              children: val(sender.categoryName || sender.category),
            },
            {
              key: 'detail',
              label: 'Барааны тайлбар',
              span: 3,
              children: val(sender.detail),
            },
            {
              key: 'hasReturn',
              label: 'Буцаагддаг бараа эсэх',
              children: <YesNo value={sender.hasReturn} />,
            },
            {
              key: 'willExpire',
              label: 'Дуусах хугацаатай эсэх',
              children: <YesNo value={sender.willExpire} />,
            },
            { key: 'minStock', label: 'Зайлшгүй байх нөөц', children: val(sender.minStock) },
          ]}
        />
      </SectionCard>

      <SectionCard icon={<BsRulers />} title="Барааны жин, эзлэхүүн">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, xl: 3 }}
          items={[
            {
              key: 'weight',
              label: 'Жин',
              children: sender.weight ? (
                <span style={{ textTransform: 'lowercase' }}>
                  {sender.weight}
                  {weightUnit}
                </span>
              ) : (
                '-'
              ),
            },
            {
              key: 'volume',
              label: sender.isCBM ? 'Эзэлхүүн' : 'Овор хэмжээ',
              children: volume ? (
                <span style={{ textTransform: 'lowercase' }}>
                  {formatVolume(volume)}
                  {sizeUnit}
                  <sup>3</sup>
                </span>
              ) : (
                '-'
              ),
            },
            ...(sender.isCBM
              ? []
              : [
                  {
                    key: 'size',
                    label: 'Урт / Өргөн / Өндөр',
                    children:
                      sender.long && sender.wide && sender.height ? (
                        <span style={{ textTransform: 'lowercase' }}>
                          {`${sender.long} × ${sender.wide} × ${sender.height} ${sizeUnit}`}
                        </span>
                      ) : (
                        '-'
                      ),
                  },
                ]),
          ]}
        />
      </SectionCard>

      <SectionCard icon={<BsImages />} title="Барааны зураг">
        <ProductImage
          alt="Барааны зураг"
          src={sender.image}
          size={86}
          preview
          style={{
            objectFit: 'contain',
            borderRadius: 8,
            background: '#fafafa',
            boxShadow: IMAGE_SHADOW,
          }}
        />
      </SectionCard>

      <SectionCard icon={<AiOutlineInfoCircle />} title="Шинж чанар, үнэ">
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 2, xl: 3 }}
          items={[
            { key: 'size', label: 'Хэмжээ', children: val(sender.size) },
            { key: 'color', label: 'Өнгө', children: val(sender.color) },
            { key: 'type', label: 'Төрөл', children: val(sender.type) },
            {
              key: 'isFragile',
              label: 'Эвдрэх аюултай эсэх',
              children: <YesNo value={sender.isFragile} />,
            },
            {
              key: 'price',
              label: 'Зарах үнэ',
              children: sender.price ? (
                <Text strong>{formatCurrency(parseInt(sender.price))}</Text>
              ) : (
                '-'
              ),
            },
            {
              key: 'sku',
              label: 'SKU код',
              children: val(isErlian ? sender.barCode?.[0] : productContext.productBaseSku),
            },
          ]}
        />
      </SectionCard>

      <FormFooter>
        {current > 0 && <Button onClick={() => prev()}>Буцах</Button>}
        {current === steps.length - 1 && (
          <Button type="primary" onClick={onHandleSubmit} loading={submitting}>
            Хадгалах
          </Button>
        )}
      </FormFooter>
    </Space>
  );
};

export default ProductCheck;
